import type {ReactNode} from 'react';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Separator} from '@common-web/components/ui/separator';
import {
  RfcAchForm,
  RfcClientPortalAccessForm,
  RfcMecForm,
  RfcSection125Form,
} from '@common/lib/rfcSchema';
import {
  Building2,
  ClipboardList,
  Users,
  Briefcase,
  MapPin,
  Phone,
  Mail,
  Banknote,
  FileText,
} from 'lucide-react';

/**
 * Identifies each section card rendered by {@link RfcReadOnlyView}. Consumers
 * can use these keys to inject a header action (e.g. an Edit button) or
 * replace a section's read-only content with an editable form.
 */
export type RfcReviewSection =
  | 'section125GroupInfo'
  | 'section125Admin'
  | 'section125Contacts'
  | 'section125Payroll'
  | 'section125Agent'
  | 'mecGroupInfo'
  | 'mecAdmin'
  | 'mecContacts'
  | 'mecPayroll'
  | 'mecAgent'
  | 'clientPortalAccess';

type Address = {
  street: string;
  city: string;
  state: string;
  zip: string;
};

type Contact = {
  firstName: string;
  lastName: string;
  title: string;
  phone: string;
  email: string;
};

function Field({
  label,
  value,
}: {
  label: string;
  value: string | number | boolean | null | undefined;
}) {
  const display =
    value === undefined || value === null || value === ''
      ? '—'
      : typeof value === 'boolean'
        ? value
          ? 'Yes'
          : 'No'
        : String(value);
  return (
    <div className='grid grid-cols-1 md:grid-cols-3 gap-2 py-1 min-w-0'>
      <div className='text-xs font-semibold text-brand-gray uppercase tracking-wider'>{label}</div>
      <div className='md:col-span-2 min-w-0 text-brand-navy font-medium break-words'>{display}</div>
    </div>
  );
}

function AddressBlock({label, address}: {label: string; address: Address | undefined}) {
  return (
    <div className='space-y-2'>
      <div className='text-sm font-semibold text-brand-navy'>{label}</div>
      <div className='flex items-start gap-3'>
        <MapPin className='h-4 w-4 mt-0.5 text-brand-blue' />
        <div className='text-sm'>
          <div>{address?.street || '—'}</div>
          <div>
            {[address?.city, address?.state, address?.zip].filter(Boolean).join(', ') || '—'}
          </div>
        </div>
      </div>
    </div>
  );
}

function ContactBlock({label, contact}: {label: string; contact: Contact | undefined}) {
  return (
    <div className='space-y-2'>
      <div className='text-sm font-semibold text-brand-navy'>{label}</div>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <Field label='First Name' value={contact?.firstName} />
        <Field label='Last Name' value={contact?.lastName} />
        <Field label='Title' value={contact?.title} />
        <div className='grid grid-cols-1 md:grid-cols-3 gap-2 py-1 min-w-0'>
          <div className='text-xs font-semibold text-brand-gray uppercase tracking-wider'>
            Phone
          </div>
          <div className='md:col-span-2 min-w-0 text-brand-navy font-medium break-words flex items-center gap-2'>
            <Phone className='h-4 w-4 shrink-0 text-brand-gray' />
            <span className='min-w-0 flex-1 break-words'>{contact?.phone || '—'}</span>
          </div>
        </div>
        <div className='grid grid-cols-1 md:grid-cols-3 gap-2 py-1 min-w-0'>
          <div className='text-xs font-semibold text-brand-gray uppercase tracking-wider'>
            Email
          </div>
          <div className='md:col-span-2 min-w-0 text-brand-navy font-medium break-words flex items-center gap-2'>
            <Mail className='h-4 w-4 shrink-0 text-brand-gray' />
            <span className='min-w-0 flex-1 break-words'>{contact?.email || '—'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function LocationsList({
  locations,
}: {
  locations: RfcSection125Form['groupInfo']['locations'] | undefined;
}) {
  if (!locations || locations.length === 0) return null;
  return (
    <div className='space-y-4'>
      <div className='text-lg font-semibold text-brand-navy'>Locations</div>
      {locations.map((loc, idx) => (
        <div
          key={idx}
          className='rounded-lg border border-gray-200 bg-gray-50 p-4 shadow-sm space-y-3'
        >
          <div className='flex items-center gap-2 font-medium text-brand-navy'>
            <Building2 className='h-4 w-4 text-brand-blue' />
            Location name: {loc.locationName || `Location ${idx + 1}`}
          </div>
          <div className='text-sm text-brand-navy'>FEIN: {loc.fein || '—'}</div>
          <div className='text-sm text-brand-navy'>
            Payroll Location Code: {loc.payrollLocationCode || '—'}
          </div>
          <div className='space-y-4'>
            <AddressBlock label='Company Address' address={loc.companyAddress} />
            <AddressBlock label='Mailing Address' address={loc.mailingAddress} />
            <ContactBlock label='Contact' contact={loc.contactPerson} />
          </div>
          {hasAchData(loc.achForm) && <AchBlock achForm={loc.achForm} />}
        </div>
      ))}
    </div>
  );
}

function SectionCard({
  title,
  icon,
  action,
  children,
}: {
  title: string;
  icon?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card className='border-gray-200 shadow-sm'>
      <CardHeader className='bg-gray-50 border-b'>
        <div className='flex items-center justify-between gap-3'>
          <CardTitle className='text-lg font-semibold text-brand-navy flex items-center gap-2'>
            {icon}
            {title}
          </CardTitle>
          {action}
        </div>
      </CardHeader>
      <CardContent className='space-y-4 pt-5'>{children}</CardContent>
    </Card>
  );
}

function hasAchData(achForm: RfcAchForm | undefined): boolean {
  if (!achForm) return false;
  return !!(achForm.achDetails?.bankName || achForm.achDetails?.routingNumber);
}

function AchBlock({achForm}: {achForm: RfcAchForm}) {
  return (
    <div className='mt-4 space-y-4 border-t border-gray-200 pt-4'>
      <div className='text-sm font-semibold text-brand-navy flex items-center gap-2'>
        <Banknote className='h-4 w-4 text-brand-blue' />
        ACH / Banking
      </div>
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <Field
          label='Business Name on Account'
          value={achForm.bankAccount?.businessNameOnAccount}
        />
        <Field
          label='Account Holder First Name'
          value={achForm.bankAccount?.accountHolderFirstName}
        />
        <Field
          label='Account Holder Last Name'
          value={achForm.bankAccount?.accountHolderLastName}
        />
        <Field label='Contact Phone' value={achForm.bankAccount?.contactPhone} />
        <Field label='Contact Email' value={achForm.bankAccount?.contactEmail} />
      </div>
      <AddressBlock label='Account Address' address={achForm.bankAccount?.address} />
      <Separator />
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <Field label='Bank Name' value={achForm.achDetails?.bankName} />
        <Field label='Routing Number' value={achForm.achDetails?.routingNumber} />
        <Field label='Account Number' value={achForm.achDetails?.accountNumber} />
      </div>
      <AddressBlock label='Bank Address' address={achForm.achDetails?.bankAddress} />
      <Separator />
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <Field label='Authorized Signature' value={achForm.authorization?.authorizedSignature} />
        <Field label='Signature Date' value={achForm.authorization?.signatureDate} />
      </div>
    </div>
  );
}

export function RfcReadOnlyView({
  data,
  showEnrollmentType = false,
  showPepm = false,
  showClientPortalAccess = true,
  sectionHeaderAction,
  sectionContentOverride,
}: {
  data: {
    section125FormEnabled: boolean;
    mecFormEnabled: boolean;
    section125Form: RfcSection125Form;
    mecForm: RfcMecForm;
    achForm?: RfcAchForm;
    clientPortalAccessForm: RfcClientPortalAccessForm;
  };
  showEnrollmentType?: boolean;
  showPepm?: boolean;
  /**
   * Tenant-level toggle (`Client.features.rfcClientPortalAccess`). When false the
   * Client Portal Access section is hidden even for RFCs that already captured
   * users, so disabling the step retroactively hides it on existing records.
   */
  showClientPortalAccess?: boolean;
  /** Renders in a section card's header (right side) — e.g. an Edit toggle. */
  sectionHeaderAction?: (section: RfcReviewSection) => ReactNode;
  /**
   * When it returns a node for a section, that node replaces the section's
   * read-only content (e.g. an inline editable form).
   */
  sectionContentOverride?: (section: RfcReviewSection) => ReactNode;
}) {
  const s125 = data.section125FormEnabled ? data.section125Form : undefined;
  const mec = data.mecFormEnabled ? data.mecForm : undefined;
  const clientPortalAccess = showClientPortalAccess
    ? data.clientPortalAccessForm || undefined
    : undefined;
  const clientPortalAccessAction = sectionHeaderAction?.('clientPortalAccess');

  return (
    <div className='space-y-8'>
      {!!s125 && (
        <div className='space-y-6'>
          <SectionCard
            title='125 Program – Group Information'
            icon={<Building2 className='h-5 w-5 text-brand-blue' />}
            action={sectionHeaderAction?.('section125GroupInfo')}
          >
            {sectionContentOverride?.('section125GroupInfo') ?? (
              <>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <Field label='Benefit Year' value={s125.groupInfo?.benefitYear} />
                  <Field label='Company Legal Name' value={s125.groupInfo?.companyName} />
                  <Field label='Company DBA' value={s125.groupInfo?.companyDba} />
                  <Field label='Other Known Name' value={s125.groupInfo?.otherKnownName} />
                  <Field label='SIC Code' value={s125.groupInfo?.sic} />
                  <Field
                    label='Primary State of Doing Business'
                    value={s125.groupInfo?.stateOfDoingBusiness}
                  />
                  <Field label='Total # of Employees' value={s125.groupInfo?.totalEmployees} />
                  <Field label='Number of Locations' value={s125.groupInfo?.numberOfLocations} />
                </div>
                <Separator />
                <LocationsList locations={s125.groupInfo?.locations} />
              </>
            )}
          </SectionCard>
          <SectionCard
            title='125 Program – Eligibility & Admin'
            icon={<ClipboardList className='h-5 w-5 text-brand-blue' />}
            action={sectionHeaderAction?.('section125Admin')}
          >
            {sectionContentOverride?.('section125Admin') ?? (
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <Field
                  label='Full Time Employees are Eligible'
                  value={s125.admin?.eligibility?.fullTime}
                />
                <Field
                  label='Part Time Employees are Eligible'
                  value={s125.admin?.eligibility?.partTime}
                />
                <Field
                  label='Waiting period for newly hired employees (days)'
                  value={s125.admin?.waitingPeriod}
                />
                <Field
                  label='Payroll Frequencies'
                  value={s125.admin?.payrollFrequencies
                    ?.map((x) => x.replace(/_/g, ' ').toLowerCase())
                    ?.join(', ')}
                />
                <Field label='Enrollment Company' value={s125.admin?.enrollmentCompany} />
                {showEnrollmentType && (
                  <Field
                    label='Enrollment Type'
                    value={s125.admin?.enrollmentType.replace(/_/g, ' ').toLowerCase()}
                  />
                )}
              </div>
            )}
          </SectionCard>
          <SectionCard
            title='125 Program – Contacts'
            icon={<Users className='h-5 w-5 text-brand-blue' />}
            action={sectionHeaderAction?.('section125Contacts')}
          >
            {sectionContentOverride?.('section125Contacts') ?? (
              <div className='space-y-6'>
                <ContactBlock
                  label='Authorized Signer (Plan Administrator)'
                  contact={s125.contacts?.authorizedSigner}
                />
                <Separator />
                <ContactBlock
                  label='Eligibility Contact'
                  contact={s125.contacts?.eligibilityContact}
                />
                <Separator />
                <ContactBlock label='Billing Contact' contact={s125.contacts?.billingContact} />
              </div>
            )}
          </SectionCard>

          <SectionCard
            title='125 Program – Payroll'
            icon={<Briefcase className='h-5 w-5 text-brand-blue' />}
            action={sectionHeaderAction?.('section125Payroll')}
          >
            {sectionContentOverride?.('section125Payroll') ?? (
              <>
                <ContactBlock
                  label='Internal Payroll Contact'
                  contact={s125.payrollInfo?.contact}
                />
                <Separator />
                <ContactBlock
                  label='Payroll Vendor Contact'
                  contact={s125.payrollInfo?.payrollVendorContact}
                />
                <Separator />
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <Field label='Payroll Vendor' value={s125.payrollInfo?.vendor} />
                  <Field label='Payroll System' value={s125.payrollInfo?.system} />
                  <Field label='Payroll Company ID' value={s125.payrollInfo?.payrollCompanyId} />
                </div>
              </>
            )}
          </SectionCard>

          <SectionCard
            title='125 Program – Agent of Record'
            icon={<FileText className='h-5 w-5 text-brand-blue' />}
            action={sectionHeaderAction?.('section125Agent')}
          >
            {sectionContentOverride?.('section125Agent') ?? (
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <Field label='Agent First Name' value={s125.agentOfRecord?.firstName} />
                <Field label='Agent Last Name' value={s125.agentOfRecord?.lastName} />
                <Field label='Phone' value={s125.agentOfRecord?.phone} />
                <Field label='Email' value={s125.agentOfRecord?.email} />
                <Field label='Additional Phone' value={s125.agentOfRecord?.additionalPhone} />
                <Field label='Additional Email' value={s125.agentOfRecord?.additionalEmail} />
                {showPepm && <Field label='PEPM' value={s125.agentOfRecord?.pepm} />}
              </div>
            )}
          </SectionCard>
        </div>
      )}

      {!!mec && (
        <div className='space-y-6'>
          <SectionCard
            title='MEC Plan – Group Information'
            icon={<Building2 className='h-5 w-5 text-brand-blue' />}
            action={sectionHeaderAction?.('mecGroupInfo')}
          >
            {sectionContentOverride?.('mecGroupInfo') ?? (
              <>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <Field label='Benefit Year' value={mec.groupInfo?.benefitYear} />
                  <Field label='Company Legal Name' value={mec.groupInfo?.companyName} />
                  <Field label='Company DBA' value={mec.groupInfo?.companyDba} />
                  <Field label='Other Known Name' value={mec.groupInfo?.otherKnownName} />
                  <Field label='SIC Code' value={mec.groupInfo?.sic} />
                  <Field
                    label='Primary State of Doing Business'
                    value={mec.groupInfo?.stateOfDoingBusiness}
                  />
                  <Field label='Total # of Employees' value={mec.groupInfo?.totalEmployees} />
                  <Field label='Number of Locations' value={mec.groupInfo?.numberOfLocations} />
                </div>
                <Separator />
                <LocationsList locations={mec.groupInfo?.locations} />
              </>
            )}
          </SectionCard>

          <SectionCard
            title='MEC Plan – Eligibility & Admin'
            icon={<ClipboardList className='h-5 w-5 text-brand-blue' />}
            action={sectionHeaderAction?.('mecAdmin')}
          >
            {sectionContentOverride?.('mecAdmin') ?? (
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <Field label='Eligible: Full Time' value={mec.admin?.eligibility?.fullTime} />
                <Field label='Eligible: Part Time' value={mec.admin?.eligibility?.partTime} />
                <Field
                  label='Waiting period for newly hired employees (days)'
                  value={mec.admin?.waitingPeriod}
                />
                <Field
                  label='Payroll Frequencies'
                  value={mec.admin?.payrollFrequencies
                    ?.map((x) => x.replace(/_/g, ' ').toLowerCase())
                    ?.join(', ')}
                />
                <Field label='Enrollment Company' value={mec.admin?.enrollmentCompany} />
                {showEnrollmentType && (
                  <Field
                    label='Enrollment Type'
                    value={mec.admin?.enrollmentType.replace(/_/g, ' ').toLowerCase()}
                  />
                )}
              </div>
            )}
          </SectionCard>
          <SectionCard
            title='MEC Plan – Contacts'
            icon={<Users className='h-5 w-5 text-brand-blue' />}
            action={sectionHeaderAction?.('mecContacts')}
          >
            {sectionContentOverride?.('mecContacts') ?? (
              <div className='space-y-6'>
                <ContactBlock
                  label='Authorized Signer (Plan Administrator)'
                  contact={mec.contacts?.authorizedSigner}
                />
                <Separator />
                <ContactBlock
                  label='Eligibility Contact'
                  contact={mec.contacts?.eligibilityContact}
                />
                <Separator />
                <ContactBlock label='Billing Contact' contact={mec.contacts?.billingContact} />
              </div>
            )}
          </SectionCard>
          <SectionCard
            title='MEC Plan – Payroll'
            icon={<Briefcase className='h-5 w-5 text-brand-blue' />}
            action={sectionHeaderAction?.('mecPayroll')}
          >
            {sectionContentOverride?.('mecPayroll') ?? (
              <>
                <ContactBlock label='Internal Payroll Contact' contact={mec.payrollInfo?.contact} />
                <Separator />
                <ContactBlock
                  label='Payroll Vendor Contact'
                  contact={mec.payrollInfo?.payrollVendorContact}
                />
                <Separator />
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                  <Field label='Payroll Vendor' value={mec.payrollInfo?.vendor} />
                  <Field label='Payroll System' value={mec.payrollInfo?.system} />
                  <Field label='Payroll Company ID' value={mec.payrollInfo?.payrollCompanyId} />
                </div>
              </>
            )}
          </SectionCard>
          <SectionCard
            title='MEC Plan – Agent of Record'
            icon={<FileText className='h-5 w-5 text-brand-blue' />}
            action={sectionHeaderAction?.('mecAgent')}
          >
            {sectionContentOverride?.('mecAgent') ?? (
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <Field label='Agent First Name' value={mec.agentOfRecord?.firstName} />
                <Field label='Agent Last Name' value={mec.agentOfRecord?.lastName} />
                <Field label='Phone' value={mec.agentOfRecord?.phone} />
                <Field label='Email' value={mec.agentOfRecord?.email} />
                <Field label='Additional Phone' value={mec.agentOfRecord?.additionalPhone} />
                <Field label='Additional Email' value={mec.agentOfRecord?.additionalEmail} />
                {showPepm && <Field label='PEPM' value={mec.agentOfRecord?.pepm} />}
              </div>
            )}
          </SectionCard>
        </div>
      )}
      {!!clientPortalAccess &&
        (!!clientPortalAccess.users?.length || !!clientPortalAccessAction) && (
          <div className='space-y-6'>
            <SectionCard
              title='Client Portal Access – Users'
              icon={<Users className='h-5 w-5 text-brand-blue' />}
              action={clientPortalAccessAction}
            >
              {sectionContentOverride?.('clientPortalAccess') ?? (
                <div className='space-y-4'>
                  {!clientPortalAccess.users?.length && (
                    <div className='text-sm text-brand-gray'>No users added.</div>
                  )}
                  {clientPortalAccess.users?.map((user, idx) => (
                    <div
                      key={idx}
                      className='rounded-lg border border-gray-200 bg-gray-50 p-4 shadow-sm space-y-3'
                    >
                      <div className='flex items-center gap-2 font-medium text-brand-navy'>
                        User {idx + 1}
                      </div>
                      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                        <Field label='First Name' value={user?.firstName} />
                        <Field label='Last Name' value={user?.lastName} />
                        <Field label='Title' value={user?.title} />
                        <Field label='Phone' value={user?.phone} />
                        <Field label='Email' value={user?.email} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </div>
        )}
    </div>
  );
}
